import React, { useEffect, useRef, useState } from "react";
import { useHotelReview } from "../../hooks/useHotelReview";
import { ChevronRightIcon, ChevronUpIcon } from "../../../../helper/HwIcon";
import { useHotelUtils } from "../../hooks/useHotelUtils";
import Slider from "react-slick";
const settings = {
  dots: false,
  arrows: false,
  infinite: false,
  speed: 500,
  slidesToShow: 2,
  slidesToScroll: 1,
  responsive: [
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
};

const getRatingLabel = (score) => {
  if (score >= 4.5) return "Excellent";
  if (score >= 4.0) return "Very Good";
  if (score >= 3.0) return "Good";
  if (score >= 2.0) return "Fair";
  if (score >= 1.0) return "Poor";
  return "";
};

export const HotelReview = ({ hotel, component, onAdditionalDataReceived, searchContext, searchCompleted }) => {
  const { avg, total, loading, refresh, data, grouped, additionalData } = useHotelReview();
  const containerRef = useRef(null);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!searchCompleted) return;
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasFetched.current) {
            hasFetched.current = true;
            observer.disconnect();

            const additionalHotelData = {
              cityName: searchContext?.searchWith || hotel?.cityName,
              countryName: searchContext?.countryOfResidence || hotel?.countryName,
              lat: hotel?.lat || searchContext?.lat,
              lang: hotel?.lang || searchContext?.lang,
              checkinDate: searchContext?.checkinDate,
              checkoutDate: searchContext?.checkoutDate,
              amount: hotel?.amount,
              roomInfo: searchContext?.roomInfo,
              currency: hotel?.currency,
              vendor: hotel?.vendor,
              rating: hotel?.rating,
              address: hotel?.address,
              ...searchContext
            };

            refresh(hotel?.id, hotel?.name, hotel?.vendor, additionalHotelData, hotel);
          }
        });
      },
      { rootMargin: "3000px" } 
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hotel?.id, hotel?.vendor, searchCompleted]);


  useEffect(() => {
    if (!loading && onAdditionalDataReceived) {
      if (additionalData && Object.keys(additionalData).length > 0) {
        onAdditionalDataReceived(additionalData, false);
      } else {
        onAdditionalDataReceived({}, true);
      }
    }
  }, [loading, additionalData, onAdditionalDataReceived]);

  return (
    <div ref={containerRef}>
      {(loading || !searchCompleted) ? (
        <span
          style={{ minWidth: "2.8rem", minHeight: "2.8rem" }}
          className="hw-main--loaderCommon"
        />
      ) : component ? (
        component(getRatingLabel(avg), avg, total, data, grouped)
      ) : (
        <>
          <div className="d-flex align-items-center hw-gapy10">
            <span className="hw-gray-900 hw-fontWeight--500">
              {getRatingLabel(avg)}
            </span>
            <span className="hw-rating--value green-bg">{avg ?? "0"}/5</span>
          </div>
          <div className="hw-text12 text-right mt-1">
            ({total ?? 0} Reviews)
          </div>
          <div className="hw-rating--toolTip">
            Based on <b>{avg ?? 0}</b> Ratings and <b>{total ?? 0}</b> reviews
          </div>
        </>
      )}
    </div>
  );
};

export const HotelReviewComponent = React.memo(
  HotelReview,
  (p, n) => p?.hotel?.id === n?.hotel?.id && p?.searchCompleted === n?.searchCompleted
);

export const HotelReviewDetails = ({ data }) => {
  const [readMore, setReadMore] = useState(false);

  const toggleReadMoreReadLess = () => {
    setReadMore(!readMore);
  };

  const { ratings } = useHotelUtils({ rating: data?.rating });

  return (

    <div className="hw-review--box">
      <div className="hw-review--top">
        <span className="hw-review--avtar">
          {data?.authorAttribution?.displayName?.[0] || data?.authorAttribution?.[0]}
        </span>
        <div className="hw-review--user">
          {data?.authorAttribution?.displayName || data?.authorAttribution}
        </div>
        <div>
          {ratings?.map(() => {
            return <span class="rating-star">★</span>;
          })}
        </div>
      </div>
      <div
        className={`hw-review--text mt-16${readMore ? " hw-read--more" : ""}`}
      >
        {data?.originalText?.text || data?.originalText}
      </div>
      <div
        className="hw-bprimary--link mt-1"
        onClick={toggleReadMoreReadLess}
      >
        {readMore ? "Read Less" : "Read More"}
        {readMore ? <ChevronUpIcon /> : <ChevronRightIcon />}
      </div>
    </div>

  );
};

export const HotelDetailsReview = ({ grouped, total, data, hotelName }) => {
  if (!grouped?.length || !data?.length || total <= 0) {
    return <div id="hotel_guest_reviews" className="hw-hotel--singleListSection" >
      <h2 className="h4 mb-2">Guest Reviews</h2>
      <p className="hw-gray-600 hw-fontSize--14">
        No guest reviews available yet.
      </p>
    </div>
  };
  return (
    <div id="hotel_guest_reviews" className="hw-hotel--singleListSection" style={{marginTop: "50px"}}>
      <div className="row hw-gapx16">
        <div className="col-sm-4 col-md-4 col-lg-3">
          <div className="hw-rating--sectionCol">
            <h2 className="h4 mb-2">Guest Reviews</h2>
            {
              grouped?.length ? <div className="mt-auto">
                <div className="hw-rating--graph">
                  {grouped?.map?.((g) => {
                    return (
                      <div className="hw-rating--graphRow">
                        <div className="d-flex align-items-center hw-gapy4 flex-shrink--null">
                          <span className="hw-graph--text hw-gray-900 hw-fontWeight--500">
                            {g?.group}
                          </span>
                          <span class="rating-star">★</span>
                        </div>
                        <div className="hw-rating--graphBar">
                          <div
                            className="hw-rating--graphBarProgress"
                            style={{ width: `${g?.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="d-flex justify-between align-items-center hw-gapy4 mt-20">
                  <button className="hw-btn hw-btn--small hw-btn--bhsecondary flex-shrink--null"
                    onClick={() => {
                      data?.[0]?.placesId ? window.open(`https://search.google.com/local/reviews?placeid=${data?.[0]?.placesId}`) : window.open(`https://www.google.com/travel/search?q=${hotelName}`)
                    }}
                  >
                    Read all reviews
                  </button>
                  <span className="hw-fontWeight--500 text-right hw-text--hidden">
                    {total} Reviews
                  </span>
                </div>
              </div> : ""
            }
          </div>
        </div>
        <div className="col-sm-8 col-md-8 col-lg-9">

          <Slider
            {...settings}
          >
            {data?.map((d) => {
              return <HotelReviewDetails data={d} />;
            })}
          </Slider>
        </div>
      </div>
    </div>
  );
};